/**
 * P0.7 — exportar relatório gerado 100% client-side (sem servidor).
 * No web: download via Blob. No app nativo (Capacitor): grava em arquivo e abre
 * a folha de compartilhamento. Export é sempre ação EXPLÍCITA do usuário (P0.8).
 */
import { Capacitor } from "@capacitor/core";
import { ESPECIALIDADE_LABEL, ORIGEM_LABEL, PASSO_ESCADA_LABEL } from "../types";
import type { Alimento, Oferta } from "../types";

function csvCampo(v: string | number | null | undefined): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** CSV legível por um profissional, sem jargão do app. Separador ";" (Excel PT-BR). */
export function ofertasParaCSV(ofertas: Oferta[], alimentos: Alimento[]): string {
  const nome = (id: string) => alimentos.find((a) => a.id === id)?.nome ?? id;
  const cab = [
    "data e hora",
    "alimento",
    "reação (passo)",
    "passo nº",
    "% comido",
    "refeição",
    "local",
    "quem ofereceu",
    "humor antes",
    "origem",
    "profissional",
    "especialidade",
    "confirmado",
    "notas",
  ];
  const linhas = ofertas
    .slice()
    .sort((a, b) => a.data_hora.localeCompare(b.data_hora))
    .map((o) =>
      [
        new Date(o.data_hora).toLocaleString("pt-BR"),
        nome(o.alimento_id),
        PASSO_ESCADA_LABEL[o.passo_escada],
        o.passo_escada,
        o.porcao_comida_pct,
        o.refeicao,
        o.local,
        o.quem_ofereceu,
        o.humor_antes,
        ORIGEM_LABEL[o.origem],
        o.profissional_nome ?? null,
        o.profissional_especialidade
          ? ESPECIALIDADE_LABEL[o.profissional_especialidade]
          : null,
        o.confirmado ? "sim" : "pendente",
        o.notas,
      ]
        .map(csvCampo)
        .join(";"),
    );
  return [cab.join(";"), ...linhas].join("\r\n");
}

/** Dispara o export do CSV: nativo (share) ou web (download). */
export async function baixarCSV(conteudo: string, arquivo: string): Promise<void> {
  const comBom = "﻿" + conteudo;

  if (Capacitor.isNativePlatform()) {
    const { Filesystem, Directory, Encoding } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    await Filesystem.writeFile({
      path: arquivo,
      data: comBom,
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    });
    const { uri } = await Filesystem.getUri({
      path: arquivo,
      directory: Directory.Cache,
    });
    await Share.share({ title: arquivo, url: uri });
    return;
  }

  const blob = new Blob([comBom], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = arquivo;
  a.click();
  URL.revokeObjectURL(url);
}

/** Uint8Array → base64 (em blocos, pra não estourar o stack com arquivos grandes). */
function bytesParaBase64(bytes: Uint8Array): string {
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

/** Dispara o export de um arquivo binário (ex.: .sqlite): nativo (share) ou web (download). */
export async function baixarBinario(
  bytes: Uint8Array,
  arquivo: string,
  mime = "application/octet-stream",
): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    await Filesystem.writeFile({
      path: arquivo,
      data: bytesParaBase64(bytes), // sem encoding = base64 (binário)
      directory: Directory.Cache,
    });
    const { uri } = await Filesystem.getUri({ path: arquivo, directory: Directory.Cache });
    await Share.share({ title: arquivo, url: uri });
    return;
  }

  // cast: Uint8Array é um BlobPart válido em runtime (o lib.dom do TS 5.7 implica
  // SharedArrayBuffer possível no genérico, daí o cast).
  const blob = new Blob([bytes as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = arquivo;
  a.click();
  URL.revokeObjectURL(url);
}
